import { useRef, useState } from "react";
import api from "@/lib/api";

const MIN_VIDEO_SECONDS = 30;

async function localVideoDuration(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const video = document.createElement("video");
        const finish = (callback, value) => {
            URL.revokeObjectURL(url);
            video.removeAttribute("src");
            callback(value);
        };
        video.preload = "metadata";
        video.onloadedmetadata = () => finish(resolve, Number(video.duration) || 0);
        video.onerror = () => finish(reject, new Error("No se pudo comprobar la duración del vídeo"));
        video.src = url;
    });
}

/**
 * Generic Cloudinary signed upload helper.
 * 1. Asks /api/media/sign for signature.
 * 2. POSTs the file to Cloudinary directly (signed).
 * 3. Returns Cloudinary response (secure_url, public_id, resource_type, duration, etc.).
 */
export async function uploadToCloudinary(file, { folder = "zentra", resourceType = "auto" } = {}, onProgress) {
    if (!file) throw new Error("No file");
    const isVideo = file.type.startsWith("video/");
    const rt = resourceType === "auto" ? (isVideo ? "video" : "image") : resourceType;

    const { data: sig } = await api.post("/media/sign", { folder, resource_type: rt });
    const fd = new FormData();
    fd.append("file", file);
    fd.append("api_key", sig.api_key);
    fd.append("timestamp", sig.timestamp);
    fd.append("signature", sig.signature);
    fd.append("folder", folder);

    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", sig.upload_url);
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded * 100) / e.total));
        };
        xhr.onload = () => {
            try {
                const res = JSON.parse(xhr.responseText);
                if (xhr.status >= 200 && xhr.status < 300) resolve(res);
                else reject(new Error(res.error?.message || `Cloudinary error ${xhr.status}`));
            } catch (e) {
                reject(e);
            }
        };
        xhr.onerror = () => reject(new Error("Network error subiendo a Cloudinary"));
        xhr.send(fd);
    });
}

/**
 * Reusable button with progress and preview support.
 * Returns the saved media item via onUploaded(item).
 */
export default function MediaUpload({
    type, // photo | video_before | video_after
    pointId,
    label,
    accept,
    folder,
    onUploaded,
    existing,
    onDelete,
    taskId,
    readOnly = false,
}) {
    const inputRef = useRef(null);
    const [progress, setProgress] = useState(null);
    const [error, setError] = useState("");

    const handlePick = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setError("");
        setProgress(0);
        try {
            const isVideo = type.startsWith("video");
            if (isVideo) {
                const duration = await localVideoDuration(file);
                if (duration < MIN_VIDEO_SECONDS) {
                    throw new Error(`El vídeo debe durar al menos ${MIN_VIDEO_SECONDS} segundos`);
                }
            }
            const cloud = await uploadToCloudinary(file, {
                folder: folder || `zentra/tasks/${taskId || "misc"}`,
                resourceType: isVideo ? "video" : "image",
            }, setProgress);
            const item = {
                type,
                secure_url: cloud.secure_url,
                public_id: cloud.public_id,
                resource_type: cloud.resource_type,
                point_id: pointId || null,
                width: cloud.width,
                height: cloud.height,
                duration: cloud.duration,
                bytes: cloud.bytes,
            };
            if (taskId) {
                const { data: saved } = await api.post(`/tasks/${taskId}/media`, item);
                onUploaded?.(saved);
            } else {
                onUploaded?.(item);
            }
        } catch (err) {
            setError(err.message || "Error al subir");
        } finally {
            setProgress(null);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const remove = async () => {
        if (!existing) return;
        if (!window.confirm("¿Borrar este archivo?")) return;
        if (taskId && existing.id) {
            await api.delete(`/tasks/${taskId}/media/${existing.id}`);
        }
        onDelete?.(existing);
    };

    return (
        <div className="relative">
            <input
                ref={inputRef}
                type="file"
                accept={accept || (type.startsWith("video") ? "video/*" : "image/*")}
                capture={type.startsWith("video") ? "environment" : "environment"}
                onChange={handlePick}
                disabled={readOnly}
                className="hidden"
                data-testid={`media-input-${type}${pointId ? `-${pointId}` : ""}`}
            />
            {existing ? (
                <div className="relative group">
                    {type.startsWith("video") ? (
                        <video
                            src={existing.secure_url}
                            controls
                            className="w-full aspect-video rounded-lg bg-black"
                            data-testid={`media-preview-${type}`}
                        />
                    ) : (
                        <img
                            src={existing.secure_url}
                            alt={label}
                            className="w-full aspect-square object-cover rounded-lg border border-zentra-border"
                            data-testid={`media-preview-${pointId || "photo"}`}
                        />
                    )}
                    {!readOnly && <button
                        type="button"
                        onClick={remove}
                        className="absolute top-1.5 right-1.5 bg-red-600 text-white text-[10px] font-bold px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition"
                        data-testid={`media-delete-${type}-${pointId || ""}`}
                    >
                        BORRAR
                    </button>}
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => !readOnly && inputRef.current?.click()}
                    disabled={progress !== null || readOnly}
                    className="w-full aspect-square rounded-lg border-2 border-dashed border-zentra-border bg-zentra-bg/40 hover:border-zentra-primary hover:bg-zentra-bg transition flex flex-col items-center justify-center gap-1 p-3"
                    data-testid={`media-upload-${type}${pointId ? `-${pointId}` : ""}`}
                >
                    {progress !== null ? (
                        <>
                            <span className="text-2xl">⏳</span>
                            <span className="text-[10px] font-bold text-zentra-primary font-mono-zentra">{progress}%</span>
                        </>
                    ) : (
                        <>
                            <span className="text-2xl">{type.startsWith("video") ? "🎥" : "📷"}</span>
                            <span className="text-[10px] text-zentra-muted text-center leading-tight">{readOnly ? "Sin evidencia" : (label || (type.startsWith("video") ? "Grabar vídeo" : "Subir foto"))}</span>
                        </>
                    )}
                </button>
            )}
            {error && <p className="text-[10px] text-red-700 mt-1">{error}</p>}
        </div>
    );
}
